import React, { useEffect, useRef } from 'react';
import { Typography, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import Layout from '../../components/layout/Layout';
import ProjectTabs from './ProjectTabs';
import { useStore } from '../../stores/root-store-context';
import { slugify } from '../../routes/RouterUtilities';
import { canAccessProject } from '../../helpers/ProjectAccessHelper';
import { ProjectProvider } from './ProjectContext';
import { useProjectLoader } from './ProjectLoader';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface ProjectContainerLayoutProps {
  children: React.ReactNode;
  breadcrumbTitle?: string;
  onProjectLoaded?: () => void;
}

/**
 * Resolves the project named in the URL once and publishes it through `ProjectContext`; the tabs
 * and every page under this layout read it with `useCurrentProject()` instead of calling
 * `projectsStore.getBySlug` themselves (which used to fire the project list request three times).
 */
const ProjectContainerLayout: React.FC<ProjectContainerLayoutProps> = observer(({ children, breadcrumbTitle, onProjectLoaded }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectSlug, projectId, id } = useParams<{ projectSlug?: string; projectId?: string; id?: string }>();
  const { applicationStore } = useStore();

  const tenant = applicationStore.company?.Tenant || '';
  const projectIdentifier = projectSlug || projectId || id;

  const current = useProjectLoader(projectIdentifier);
  const { project, isLoading, notFound, loadError } = current;

  const accessDenied = !!project && !canAccessProject(project, applicationStore.user, 'read');
  const error = loadError
    ? t('Failed to load project')
    : notFound || accessDenied
      ? t('Project not found')
      : null;

  // Loaded by id (or a stale slug): put the current slug in the URL without a reload.
  useEffect(() => {
    if (!project || accessDenied || projectSlug) return;
    const slug = slugify(project.title || project.name || '');
    if (slug && window.location.pathname.indexOf(slug) === -1) {
      const newPath = window.location.pathname.replace(/\/projects\/[^/]+/, `/projects/${slug}`);
      navigate(newPath, { replace: true });
    }
  }, [project, accessDenied, projectSlug, navigate]);

  // Unknown slug or id: back to the list after a short delay.
  useEffect(() => {
    if (isLoading || !notFound) return;
    const timer = setTimeout(() => navigate(`/${tenant}/projects`), 3000);
    return () => clearTimeout(timer);
  }, [isLoading, notFound, navigate, tenant]);

  // Router passes a fresh arrow on every render; the ref keeps the callback out of the effect deps.
  const onLoadedRef = useRef(onProjectLoaded);
  onLoadedRef.current = onProjectLoaded;
  useEffect(() => {
    if (projectIdentifier && !isLoading) onLoadedRef.current?.();
  }, [projectIdentifier, isLoading]);

  const headerTitle = project && !accessDenied
    ? (project.name || project.title)
    : (error || (isLoading ? t('Loading project...') : breadcrumbTitle || ''));

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('Home'), path: '/' },
    { label: t('Projects'), path: `/${tenant}/projects` },
    { label: headerTitle, path: '#' },
  ];

  return (
    <Layout
      headerStyle={1}
      footerStyle={2}
      breadcrumbTitle={headerTitle}
      breadcrumbItems={breadcrumbItems}
    >
      <Helmet>
        <title>{headerTitle}</title>
        <meta name="description" content={headerTitle} />
      </Helmet>
      <div className="container-fluid" style={{ margin: "20px auto" }}>
        <Spin spinning={isLoading}>
          {error ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography.Title level={4} type="danger">{error}</Typography.Title>
              <Typography.Text>Redirecting to projects page...</Typography.Text>
            </div>
          ) : (
            <ProjectProvider value={current}>
              <ProjectTabs />
              {children}
            </ProjectProvider>
          )}
        </Spin>
      </div>
    </Layout>
  );
});

export default ProjectContainerLayout;
